/**
 * Comparator read accessors (MOTHER/FATHER migration).
 *
 * The canonical `comparators[]` array is the single source of truth for family
 * members (proband + MOTHER / FATHER / other relatives). Any residual legacy
 * `mother` / `father` slot data on an inbound payload is folded into
 * `comparators[]` by `normalizeInboundParentComparators` at the hydration
 * boundary, so the helpers here read straight off the list.
 *
 * Design reference: see
 *   `imdb_database/inflm_database/design_docs/comparator_relationship_model_design.md`
 *   §5 (`comparators[]` outbound shape).
 */

import type { CanonicalOrderPayload, Comparator, FamilyMemberRelationship } from './canonicalOrder'
import { parentSideForComparator } from './comparatorWriteAdapter'

/**
 * Derive the set of relationships present on `comparators[]` (Phase F.1b /
 * GEN-2998, AC3). This is the source-of-truth the SPA migrates toward,
 * replacing the `biologicalMother` / `biologicalFather` test-option boolean
 * pair (§9.2/§9.4 of the design doc). Order is preserved as first-seen;
 * duplicates and nullish entries are dropped. Pure and flag-agnostic — the
 * flag-gated wiring of consumers lands in the following slice.
 */
export function deriveSelectedComparatorRelationships(co: CanonicalOrderPayload): FamilyMemberRelationship[] {
  const comparators = co.comparators
  if (!Array.isArray(comparators) || comparators.length === 0) {
    return []
  }
  const seen = new Set<FamilyMemberRelationship>()
  const out: FamilyMemberRelationship[] = []
  for (const c of comparators) {
    if (c && c.relationship && !seen.has(c.relationship)) {
      seen.add(c.relationship)
      out.push(c.relationship)
    }
  }
  return out
}

/**
 * Derive the canonical `orderType` string (PROBAND_ONLY / FAMILY_DUO_MOTHER /
 * FAMILY_DUO_FATHER / FAMILY_TRIO) purely from the parent comparators present
 * on `comparators[]`.
 *
 * P12 (MOTHER/FATHER migration): the single comparator-set → tier-string
 * derivation in the SPA. It returns the four-string contract
 * (`PROBAND_ONLY` / `FAMILY_DUO_MOTHER` / `FAMILY_DUO_FATHER` / `FAMILY_TRIO`)
 * directly from the comparators set; parent rows are detected via
 * `parentSideForComparator`, which accepts both the canonical UPPER_SNAKE
 * literals and the legacy lowercase `bio_mother` / `bio_father`. Replaces
 * the prior boolean-pair-fed `computeOrderType(hasMother, hasFather)`.
 */
export function orderTypeFromComparators(co: CanonicalOrderPayload | null | undefined): string {
  if (!co || !Array.isArray(co.comparators)) return 'PROBAND_ONLY'
  let hasMother = false
  let hasFather = false
  for (const c of co.comparators) {
    if (!c) continue
    const side = parentSideForComparator(c)
    if (side === 'mother') hasMother = true
    else if (side === 'father') hasFather = true
    if (hasMother && hasFather) break
  }
  if (hasMother && hasFather) return 'FAMILY_TRIO'
  if (hasMother) return 'FAMILY_DUO_MOTHER'
  if (hasFather) return 'FAMILY_DUO_FATHER'
  return 'PROBAND_ONLY'
}

/**
 * Parent-comparator count (0 / 1 / 2) for a placed or in-progress order, taken
 * as the max of the `comparators[]`-derived count and the canonical
 * `orderType`. Decoupling (R5): downstream views must reflect the *order's*
 * family structure, never the chosen test type's `parentSamplesRequired` — a
 * multi-tier category can run a duo/trio order on a SKU whose config tier is 0.
 *
 * The max keeps both shapes safe: new orders carry parent rows on
 * `comparators[]`, while legacy orders may only have `orderType` set before the
 * comparator hydration has populated the array.
 */
export function parentComparatorCountForOrder(co: CanonicalOrderPayload | undefined | null): 0 | 1 | 2 {
  if (!co) return 0
  let fromComparators = 0
  if (Array.isArray(co.comparators)) {
    let hasMother = false
    let hasFather = false
    for (const c of co.comparators) {
      if (!c) continue
      const side = parentSideForComparator(c)
      if (side === 'mother') hasMother = true
      else if (side === 'father') hasFather = true
      if (hasMother && hasFather) break
    }
    fromComparators = (hasMother ? 1 : 0) + (hasFather ? 1 : 0)
  }
  const ot = (co.orderType ?? 'PROBAND_ONLY').trim()
  const fromOrderType = ot === 'FAMILY_TRIO' ? 2 : ot === 'FAMILY_DUO_MOTHER' || ot === 'FAMILY_DUO_FATHER' ? 1 : 0
  return Math.max(fromComparators, fromOrderType) as 0 | 1 | 2
}

/**
 * Locate the `MOTHER` / `FATHER` parent comparator on a canonical order by
 * iterating `comparators[]` and matching the relationship literal. Replaces
 * the legacy `resolveParentForRead(co, side)` slot-projection accessor and the
 * former side-keyed `findParentComparator(co, side)` (since removed) from
 * `comparatorWriteAdapter` (MOTHER/FATHER migration P7).
 *
 * Inbound payloads have any residual `mother` / `father` slot data folded
 * into `comparators[]` by `normalizeInboundParentComparators` at the slice
 * boundary, so this lookup is observably equivalent to the previous
 * resolver-shim path.
 *
 * Shared by every read-side consumer (`deriveOrderInProgressFamilyRows`,
 * `FormCompletionChecker`, `PDFRenderer`, the order pages, the hooks).
 */
export function getParentComparator(
  co: CanonicalOrderPayload | null | undefined,
  role: 'mother' | 'father'
): Comparator | undefined {
  if (!co || !Array.isArray(co.comparators)) return undefined
  return co.comparators.find((c) => c != null && parentSideForComparator(c) === role)
}
